import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ButtonControl, ContextMenuControl, IconControl, Loading } from "spark-core-dx/components";

const getActionClassName = (
  action = {},
  {
    actionClassName = "",
    actionRightClassName = "",
    actionDangerClassName = "",
    actionSeparatorAfterClassName = "",
  } = {}
) => [
  "convoke-command-bar__action",
  actionClassName,
  action.alignRight ? `convoke-command-bar__action--right ${actionRightClassName}` : "",
  action.danger ? `convoke-command-bar__action--danger ${actionDangerClassName}` : "",
  action.separatorAfter ? `convoke-command-bar__action--separator-after ${actionSeparatorAfterClassName}` : "",
  action.className || "",
].filter(Boolean).join(" ");

const ConvokeCommandBar = ({
  actions = [],
  ariaLabel = "Actions",
  className = "",
  actionClassName = "",
  actionRightClassName = "",
  actionDangerClassName = "",
  actionSeparatorAfterClassName = "",
  overflowWrapperClassName = "",
  measureClassName = "",
  menuClassName = "",
  menuActionDangerClassName = "",
  loadingClassName = "",
  useSparkButtons = false,
}) => {
  const actionBarRef = useRef(null);
  const actionBarMeasureRef = useRef(null);
  const stableActions = useMemo(
    () => (Array.isArray(actions) ? actions.filter((action) => action?.key) : []),
    [actions]
  );
  const normalActions = useMemo(
    () => stableActions.filter((action) => !action.alignRight),
    [stableActions]
  );
  const rightActions = useMemo(
    () => stableActions.filter((action) => action.alignRight),
    [stableActions]
  );
  const [visibleActionCount, setVisibleActionCount] = useState(normalActions.length);
  const [openOverflowMenu, setOpenOverflowMenu] = useState(false);
  const [overflowMenuStyle, setOverflowMenuStyle] = useState(null);
  const visibleActions = normalActions.slice(0, visibleActionCount);
  const overflowActions = normalActions.slice(visibleActionCount);
  const classNameOptions = {
    actionClassName,
    actionRightClassName,
    actionDangerClassName,
    actionSeparatorAfterClassName,
  };

  useLayoutEffect(() => {
    const updateVisibleActions = () => {
      const barWidth = actionBarRef.current?.getBoundingClientRect?.().width || Number.MAX_SAFE_INTEGER;
      const measureBar = actionBarMeasureRef.current;
      if (!measureBar || !Number.isFinite(barWidth) || barWidth <= 0) {
        setVisibleActionCount(normalActions.length);
        setOpenOverflowMenu(false);
        return;
      }

      const readWidth = (selector) => measureBar.querySelector(selector)?.getBoundingClientRect?.().width || 0;
      const normalWidths = normalActions.map((_, index) => readWidth(`[data-action-measure="normal-${index}"]`));
      const rightWidths = rightActions.map((_, index) => readWidth(`[data-action-measure="right-${index}"]`));
      const overflowWidth = readWidth("[data-action-measure='overflow']");
      const style = window.getComputedStyle?.(actionBarRef.current);
      const gap = Number.parseFloat(style?.columnGap || style?.gap || "0") || 0;
      const sum = (values) => values.reduce((total, value) => total + value, 0);
      const fits = (width, count) => width + Math.max(0, count - 1) * gap <= barWidth + 1;
      const allActionCount = normalActions.length + rightActions.length;
      const allActionsWidth = sum(normalWidths) + sum(rightWidths);
      let nextVisibleCount = normalActions.length;

      if (!fits(allActionsWidth, allActionCount)) {
        for (let visibleCount = normalActions.length; visibleCount >= 0; visibleCount -= 1) {
          const measuredWidth = sum(normalWidths.slice(0, visibleCount)) + overflowWidth + sum(rightWidths);
          const measuredCount = visibleCount + 1 + rightActions.length;
          if (fits(measuredWidth, measuredCount)) {
            nextVisibleCount = visibleCount;
            break;
          }
        }
      }

      setVisibleActionCount((current) => (current === nextVisibleCount ? current : nextVisibleCount));
      if (nextVisibleCount >= normalActions.length) {
        setOpenOverflowMenu(false);
      }
    };

    updateVisibleActions();
    const observer = typeof ResizeObserver !== "undefined" && actionBarRef.current
      ? new ResizeObserver(updateVisibleActions)
      : null;
    observer?.observe(actionBarRef.current);
    window.addEventListener("resize", updateVisibleActions);

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", updateVisibleActions);
    };
  }, [normalActions, rightActions]);

  const openOverflowActions = useCallback((event) => {
    const buttonBox = event.currentTarget?.getBoundingClientRect?.();
    setOpenOverflowMenu((current) => !current);
    setOverflowMenuStyle({
      left: Math.max(8, (buttonBox?.right ?? 0) - 160),
      top: (buttonBox?.bottom ?? 0) + 6,
    });
  }, []);

  const runOverflowAction = useCallback((action) => {
    setOpenOverflowMenu(false);
    action?.onClick?.();
  }, []);

  const renderActionIcon = useCallback((action = {}) => {
    if (action.loading) {
      return (
        <span className={`convoke-command-bar__action-loading ${loadingClassName}`.trim()} aria-hidden="true">
          <Loading type="spinner-2" size="1rem" />
        </span>
      );
    }

    if (action.iconElement) {
      return action.iconElement;
    }

    if (!action.iconKey) {
      return null;
    }

    return <IconControl iconKey={action.iconKey} width="1rem" height="1rem" />;
  }, [loadingClassName]);

  const renderActionButton = useCallback((action, actionClassNameValue, onClick) => {
    const content = (
      <>
        {renderActionIcon(action)}
        <span>{action.label}</span>
      </>
    );

    if (useSparkButtons) {
      return (
        <ButtonControl
          key={action.key}
          type="standard"
          className={actionClassNameValue}
          disabled={action.disabled}
          title={action.title}
          aria-label={action.ariaLabel}
          onClick={onClick}
        >
          {content}
        </ButtonControl>
      );
    }

    return (
      <button
        key={action.key}
        type="button"
        className={actionClassNameValue}
        disabled={action.disabled}
        title={action.title}
        aria-label={action.ariaLabel}
        onClick={onClick}
      >
        {content}
      </button>
    );
  }, [renderActionIcon, useSparkButtons]);

  return (
    <div
      ref={actionBarRef}
      className={`convoke-command-bar ${className}`.trim()}
      role="toolbar"
      aria-label={ariaLabel}
    >
      {visibleActions.map((action) => renderActionButton(
        action,
        getActionClassName(action, classNameOptions),
        action.onClick
      ))}
      {overflowActions.length > 0 ? (
        <span className={`convoke-command-bar__overflow ${overflowWrapperClassName}`.trim()}>
          {renderActionButton(
            {
              key: "convoke-command-bar-overflow",
              label: "",
              iconKey: "viewmore",
              title: "More actions",
              ariaLabel: "More actions",
            },
            getActionClassName({}, classNameOptions),
            openOverflowActions
          )}
          {openOverflowMenu ? (
            <ContextMenuControl
              openContextMenu={openOverflowMenu}
              setOpenContextMenu={setOpenOverflowMenu}
              contextMenuStyle={overflowMenuStyle}
            >
              <div className={`convoke-command-bar__menu ${menuClassName}`.trim()} role="menu" aria-label="More actions">
                {overflowActions.map((action) => (
                  <button
                    key={action.key}
                    type="button"
                    role="menuitem"
                    disabled={action.disabled}
                    className={action.danger ? menuActionDangerClassName : ""}
                    title={action.title}
                    onClick={() => runOverflowAction(action)}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            </ContextMenuControl>
          ) : null}
        </span>
      ) : null}
      {rightActions.map((action) => renderActionButton(
        action,
        getActionClassName(action, classNameOptions),
        action.onClick
      ))}
      <div
        ref={actionBarMeasureRef}
        className={`convoke-command-bar__measure ${measureClassName}`.trim()}
        aria-hidden="true"
      >
        {normalActions.map((action, index) => (
          <span
            key={`normal-${action.key}`}
            className={getActionClassName(action, classNameOptions)}
            data-action-measure={`normal-${index}`}
          >
            {renderActionIcon(action)}
            <span>{action.label}</span>
          </span>
        ))}
        <span
          className={getActionClassName({}, classNameOptions)}
          data-action-measure="overflow"
        >
          <IconControl iconKey="viewmore" width="1rem" height="1rem" />
        </span>
        {rightActions.map((action, index) => (
          <span
            key={`right-${action.key}`}
            className={getActionClassName({ ...action, alignRight: false }, classNameOptions)}
            data-action-measure={`right-${index}`}
          >
            {renderActionIcon(action)}
            <span>{action.label}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

export default ConvokeCommandBar;
